// TelcoTrack — Excel čísel, která graf nebo dashboard už ukazuje.
// Stejný sešit jako tabulky (`downloadExcelJsAoAWorkbook`).

import type { EChartsOption } from 'echarts';
import type { ManagerKpiCardId, ManagerKpiCategoryId } from '@core/shared-bridge';
import type {
  ManagerDashboardMetrics,
  ManagerDashboardTimeseriesResult,
} from '../../core/models/object.model';
import {
  buildKpiStackedBarSeriesData,
  buildManagerKpiLineChartOption,
  formatTimeseriesBucketLabel,
} from './manager-kpi-chart.echarts';
import { kpiFractionPieEligible, kpiFractionPiePercent } from './kpi-fraction-pie.echarts';
import {
  buildManagerKpiCategoryDetail,
  buildManagerKpiCategoryTiles,
  type ManagerKpiSnapshotCardView,
} from './manager-kpi-snapshot.util';
import type { FinanceProgressComparisonModel } from './finance-progress-comparison.util';
import { createBasicAoAStyleSheet, downloadExcelJsAoAWorkbook } from './xlsx-workbook.util';

export type ChartExportAoA = (string | number)[][];

export interface ChartExportSheet {
  name: string;
  aoa: ChartExportAoA;
}

export interface GanttTaskExportInput {
  vedouci: string;
  polygon: string;
  subtitle: string;
  folder: string;
  state: string;
  branch: string;
  task: string;
  term: string;
  kind: '' | 'planned' | 'actual' | 'milestone';
}

const KPI_CARD_HEADER = ['Karta', 'Hotovo', 'Celkem', 'Procento', 'Jednotka'] as const;

function plain(value: string | null | undefined): string {
  return (value ?? '').replaceAll('\u00a0', ' ').trim();
}

function shownNumber(value: number | null | undefined, label: string): string | number {
  if (value != null && Number.isFinite(value)) return value;
  const text = plain(label);
  if (!text || text === '—' || text === '-') return '';
  return text;
}

function cardLine(
  label: string,
  valueMode: ManagerKpiSnapshotCardView['valueMode'],
  numerator: number | null,
  denominator: number | null,
  numeratorLabel: string,
  denominatorLabel: string,
  unitSuffix: string,
): (string | number)[] {
  const percent =
    valueMode === 'fraction' && kpiFractionPieEligible(numerator, denominator)
      ? kpiFractionPiePercent(numerator as number, denominator as number)
      : '';
  return [
    plain(label),
    shownNumber(numerator, numeratorLabel),
    valueMode === 'absolute' ? '' : shownNumber(denominator, denominatorLabel),
    percent,
    plain(unitSuffix),
  ];
}

function rowsForCard(label: string, card: ManagerKpiSnapshotCardView): ChartExportAoA {
  const rows: ChartExportAoA = [
    cardLine(
      label,
      card.valueMode,
      card.numeratorValue,
      card.denominatorValue,
      card.numeratorLabel,
      card.denominatorLabel,
      card.unitSuffix,
    ),
  ];
  const secondary = card.secondaryPair;
  if (secondary) {
    const suffix = plain(secondary.unitSuffix) || 'HP';
    rows.push(
      cardLine(
        `${plain(label)} (${suffix})`,
        'fraction',
        secondary.numeratorValue,
        secondary.denominatorValue,
        secondary.numeratorLabel,
        secondary.denominatorLabel,
        secondary.unitSuffix,
      ),
    );
  }
  return rows;
}

/** Řádky jedné viditelné karty (a druhého zlomku HP, když ho karta ukazuje). */
export function buildLabeledKpiCardAoA(
  entries: readonly { label: string; card: ManagerKpiSnapshotCardView }[],
): ChartExportAoA {
  const rows: ChartExportAoA = [[...KPI_CARD_HEADER]];
  for (const entry of entries) {
    rows.push(...rowsForCard(entry.label, entry.card));
  }
  return rows;
}

/** Viditelné KPI karty: hub = dlaždice kategorií, detail = karty otevřené kategorie. */
export function buildManagerKpiCardsAoA(
  metrics: ManagerDashboardMetrics | null | undefined,
  timeseries: ManagerDashboardTimeseriesResult | null | undefined,
  activeCategory: ManagerKpiCategoryId | null,
): ChartExportAoA {
  if (!metrics) return [[...KPI_CARD_HEADER]];
  if (activeCategory) {
    const detail = buildManagerKpiCategoryDetail(activeCategory, metrics, timeseries ?? null);
    return buildLabeledKpiCardAoA(detail.cards.map((card) => ({ label: card.label, card })));
  }
  return buildLabeledKpiCardAoA(
    buildManagerKpiCategoryTiles(metrics, timeseries ?? null).map((tile) => ({
      label: tile.title,
      card: tile.headline,
    })),
  );
}

function lineSeriesList(series: EChartsOption['series']): Array<{ name?: string; data?: unknown }> {
  if (!series) return [];
  const list = Array.isArray(series) ? series : [series];
  return list as Array<{ name?: string; data?: unknown }>;
}

function seriesPercent(data: unknown, index: number): string | number {
  if (!Array.isArray(data)) return '';
  const value = data[index];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object' && 'value' in value) {
    const nested = Number((value as { value: unknown }).value);
    return Number.isFinite(nested) ? nested : '';
  }
  return '';
}

/** Řady čárového grafu růstu — stejné % a popisek, které graf počítá do tooltipu. */
export function buildManagerKpiGrowthAoA(
  timeseries: ManagerDashboardTimeseriesResult | null | undefined,
  seriesCardIds: readonly ManagerKpiCardId[] | null,
): ChartExportAoA {
  const header: (string | number)[] = ['Období', 'Řada', 'Procento', 'Zobrazeno'];
  const points = timeseries?.points ?? [];
  if (!timeseries || points.length === 0) return [header];
  const option = buildManagerKpiLineChartOption({
    pts: points,
    bucket: timeseries.bucket,
    dark: false,
    seriesCardIds,
  });
  const bundle = buildKpiStackedBarSeriesData(points, '#262626', false, '#e5e5e5');
  const rows: ChartExportAoA = [header];
  points.forEach((point, index) => {
    const period = formatTimeseriesBucketLabel(point.bucket, timeseries.bucket);
    for (const series of lineSeriesList(option.series)) {
      const name = plain(series.name);
      if (!name || name.startsWith('_')) continue;
      rows.push([
        period,
        name,
        seriesPercent(series.data, index),
        bundle.kpiBarLabel(name, index),
      ]);
    }
  });
  return rows;
}

export function buildManagerKpiDashboardSheets(args: {
  metrics: ManagerDashboardMetrics | null;
  timeseries: ManagerDashboardTimeseriesResult | null;
  activeCategory: ManagerKpiCategoryId | null;
  includeGrowth: boolean;
  seriesCardIds: readonly ManagerKpiCardId[] | null;
}): ChartExportSheet[] {
  const sheets: ChartExportSheet[] = [];
  const cards = buildManagerKpiCardsAoA(args.metrics, args.timeseries, args.activeCategory);
  if (cards.length > 1) sheets.push({ name: 'Karty', aoa: cards });
  if (args.includeGrowth) {
    const growth = buildManagerKpiGrowthAoA(args.timeseries, args.seriesCardIds);
    if (growth.length > 1) sheets.push({ name: 'Růst', aoa: growth });
  }
  return sheets;
}

function financeUkazateleAoA(model: FinanceProgressComparisonModel): ChartExportAoA {
  const rows: ChartExportAoA = [['Ukazatel', 'Procento', 'Popis']];
  if (model.physical) {
    rows.push(['Prostavěnost', model.physical.percent, model.physical.caption ?? '']);
  }
  if (model.income) {
    rows.push(['Plánovaný příjem', model.income.percent, model.income.caption ?? '']);
  }
  if (model.note) {
    rows.push(['Poznámka', '', model.note.text]);
  }
  return rows;
}

function financeSouhrnAoA(model: FinanceProgressComparisonModel): ChartExportAoA {
  const rows: ChartExportAoA = [['Ukazatel', 'Hodnota', 'Detail']];
  for (const line of model.summary) {
    rows.push([line.label, line.valueLabel, line.detailLabel ?? '']);
  }
  return rows;
}

function financeKategorieAoA(model: FinanceProgressComparisonModel): ChartExportAoA {
  const rows: ChartExportAoA = [['Kategorie', 'Prostavěnost', 'Plánovaný příjem']];
  for (const category of model.categories) {
    rows.push([category.label, category.physicalLabel ?? '', category.incomeLabel ?? '']);
  }
  return rows;
}

/** Donuty, milník / souhrn, sloupce kategorií a volitelně řádky tabulky plán vs skutečnost. */
export function buildFinanceChartExportSheets(
  model: FinanceProgressComparisonModel | null,
  tableAoa: ChartExportAoA,
): ChartExportSheet[] {
  const sheets: ChartExportSheet[] = [];
  if (model) {
    const ukazatele = financeUkazateleAoA(model);
    if (ukazatele.length > 1) sheets.push({ name: 'Ukazatele', aoa: ukazatele });
    const souhrn = financeSouhrnAoA(model);
    if (souhrn.length > 1) sheets.push({ name: 'Souhrn', aoa: souhrn });
    const kategorie = financeKategorieAoA(model);
    if (kategorie.length > 1) sheets.push({ name: 'Kategorie', aoa: kategorie });
  }
  if (tableAoa.length > 1) sheets.push({ name: 'Plán vs skutečnost', aoa: tableAoa });
  return sheets;
}

function ganttKindLabel(kind: GanttTaskExportInput['kind']): string {
  if (kind === 'actual') return 'Skutečnost';
  if (kind === 'milestone') return 'Milník';
  if (kind === 'planned') return 'Plán';
  return '';
}

/** Řádky Ganttu, které jsou na obrazovce (jeden řádek sešitu = jedna tyč nebo milník). */
export function buildGanttTaskExportAoA(tasks: readonly GanttTaskExportInput[]): ChartExportAoA {
  return [
    ['Vedoucí stavby', 'Polygon', 'Doplněk', 'Složka', 'Stav', 'Pobočka', 'Úkol', 'Termín', 'Typ'],
    ...tasks.map((task) => [
      plain(task.vedouci),
      plain(task.polygon),
      plain(task.subtitle),
      plain(task.folder),
      plain(task.state),
      plain(task.branch),
      plain(task.task),
      plain(task.term),
      ganttKindLabel(task.kind),
    ]),
  ];
}

export function chartExportStamp(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

/** Stáhne sešit. `false` = není co exportovat (jen hlavičky). */
export async function downloadChartWorkbook(filename: string, sheets: readonly ChartExportSheet[]): Promise<boolean> {
  const ready = sheets.filter((sheet) => sheet.aoa.length > 1);
  if (ready.length === 0) return false;
  await downloadExcelJsAoAWorkbook(
    filename,
    ready.map((sheet) => ({
      name: sheet.name,
      aoa: sheet.aoa,
      styleSheet: createBasicAoAStyleSheet(),
    })),
  );
  return true;
}
